import {
  createSlice, PayloadAction, createAsyncThunk,
} from '@reduxjs/toolkit';
import { doGetRequest, doPatchRequest } from '../../../common/request';
import { appConfig } from '../../../common/config';
import { RootState } from '../../../store';

interface DeviceSettingsState {
  // PIP Device
  device?: any;
  deviceSettings?: any;
  isLoading: boolean;
  errMsg?: string;
}

const initialState: DeviceSettingsState = {
  isLoading: false,
};

// Handle actions in your reducers
const deviceSlice = createSlice({
  name: 'device',
  initialState,
  reducers: {
    setLoadingDevice: (state) => {
      state.isLoading = true;
      state.device = undefined;
    },
    setDevice: (state, action: PayloadAction<any>) => {
      state.device = action.payload;
      state.isLoading = false;
    },
    setDeviceSettings: (state, action: PayloadAction<any>) => {
      if (action.payload.deviceTypeSettings) {
        state.deviceSettings = action.payload.deviceTypeSettings.map((setting: any) => {
          const val = action.payload.deviceStatus?.Settings?.[`Setting${setting.Id}`] ?? '';
          return {
            ...setting,
            CurrentValue: `${val}`,
            Value: `${val}`,
          };
        });
      }
      state.isLoading = false;
    },
    updateDeviceSetting: (state, action: PayloadAction<{id: string, value: any}>) => {
      const { id, value } = action.payload;
      if (state.deviceSettings) {
        const setting = state.deviceSettings.find((s: any) => s.Id === id);
        if (setting) {
          setting.Value = value;
        }
      }
    },
    setError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.device = undefined;
      state.errMsg = action.payload;
    },
  },
});

export const fetchDeviceAndSettings = createAsyncThunk<void, {deviceIdOrSerial?: string}>(
  'device/fetchDeviceAndSettings',
  async (params, thunkAPI) => {
    try {
      thunkAPI.dispatch(deviceSlice.actions.setDevice(undefined));
      if (!params.deviceIdOrSerial) {
        return;
      }
      thunkAPI.dispatch(deviceSlice.actions.setLoadingDevice());
      const device = await doGetRequest(`${appConfig.settings.pipApiBase}/devices/${params.deviceIdOrSerial}`, false);
      const deviceId = device.DeviceId;
      const customerId = device.CustomerId;

      let deviceStatus;
      try {
        deviceStatus = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${customerId}/devices/${deviceId}/status`, false);
      } catch (err) {
        console.log(err);
      }

      const deviceTypeSettings = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${customerId}/devices/${deviceId}/settings`, false);
      thunkAPI.dispatch(deviceSlice.actions.setDevice(device));
      thunkAPI.dispatch(deviceSlice.actions.setDeviceSettings({deviceTypeSettings, deviceStatus}));
    } catch (err) {
      console.log(err);
      thunkAPI.dispatch(deviceSlice.actions.setError('Device not found'));
    }
  },
);

export const updateDeviceSettings = createAsyncThunk<void>(
  'device/updateDeviceSettings',
  async (_, thunkAPI) => {
    // Get device and settings from state
    const deviceSettingsState = (thunkAPI.getState() as RootState).deviceSettings;
    const device = deviceSettingsState?.device;
    if (!device) {
      console.error('Not found device');
      return;
    }
    const deviceId = device.DeviceId;
    const customerId = device.CustomerId;
    const serialNumber = device.SerialNumber;
    if (!deviceId || !customerId || !serialNumber) {
      console.error('Invalid device');
      return;
    }
    // Prepare settings payload for update
    const settings = deviceSettingsState.deviceSettings?.filter(
      (setting: any) => setting.Value?.trim()?.length > 0 && setting.Value !== setting.CurrentValue,
    ).map((setting: any) => ({
      Id: setting.Id,
      Value: setting.Value,
    }));
    if (!settings || settings.length === 0) {
      console.error('No settings to update');
      return;
    }
    // Send update request to API
    try {
      await doPatchRequest(`${appConfig.settings.pipApiBase}/customers/${customerId}/devices/${deviceId}/settings`, settings);
    } catch (err) {
      thunkAPI.dispatch(deviceSlice.actions.setError('Device not found'));
    }
  },
);

export const {
  updateDeviceSetting,
} = deviceSlice.actions;

export default deviceSlice.reducer;
